/**
 * Streaming Message Renderer
 *
 * Real-time block detection and rendering with token buffering.
 *
 * Key features:
 * - Detects block markers as they stream and shows loading placeholders
 * - Renders blocks inline as soon as they're complete
 * - No bubble format - content flows naturally like website sections
 * - Token buffering for smooth, professional rendering
 * - "Thinking" mode: filler text streams as a subtle thinking indicator,
 *   then collapses into a toggle when server blocks arrive
 *
 * Architecture:
 * - constants.ts: Configuration values
 * - markdown.ts: Markdown parsing utilities
 * - block-parser.ts: Block detection and parsing
 * - skeleton.ts: Loading placeholder components
 */

import type { MessageBlock } from '../config';
import { renderBlock, resetRenderCycle } from '../../ui/blocks/index';
import {
  RENDER_DELAY_MS,
  MIN_BUFFER_SIZE,
  MAX_CONTENT_LENGTH,
  LOADING_DELAY_MS
} from './constants';
import { parseMarkdown, parseMarkdownStreaming } from './markdown';
import {
  isValidBlockType,
  isColonSyntaxBlockType,
  parseColonSyntaxBlock,
  parseJsonBlock,
  stripIncompleteBlockMarkers,
  findPartialBlockMarker,
  stripMalformedStreamingMarkers
} from './block-parser';
import {
  createBlockSkeleton,
  createBlockPlaceholder,
  createLoadingIndicator,
  createBlockError
} from './skeleton';
import { tryParseJSON, deepParseJSON } from './json-utils';
import { t } from '../i18n';

// =============================================================================
// STREAMING RENDERER CLASS
// =============================================================================

export class StreamingRenderer {
  private container: HTMLElement;
  private responseWrapper: HTMLElement | null = null;
  private currentTextSpan: HTMLElement | null = null;
  private content = '';
  private processedIndex = 0;
  private isFirstToken = true;
  private onAction?: (value: string) => void;

  // Block detection state
  private inBlock = false;
  private blockStartIndex = -1;
  private blockType = '';
  private blockPlaceholder: HTMLElement | null = null;
  private renderedBlockRanges: Array<{ start: number; end: number }> = [];

  // Token buffering for smoother rendering
  private tokenBuffer = '';
  private renderTimeout: ReturnType<typeof setTimeout> | null = null;

  // Lifecycle state - prevents race conditions
  private isCompleted = false;
  private isDestroyed = false;

  // Loading indicator state
  private loadingIndicator: HTMLElement | null = null;
  private loadingTimeout: ReturnType<typeof setTimeout> | null = null;

  // Track if server-sent blocks arrived (via addBlock, not inline markers)
  private hasServerBlocks = false;

  // Collected blocks for persistence — every rendered block is tracked here
  private collectedBlocks: MessageBlock[] = [];

  // Interactive block signals response is effectively complete — suppress further loading
  private interactiveReceived = false;

  // Thinking section — wraps filler text before server blocks arrive
  private thinkingWrapper: HTMLElement | null = null;

  constructor(container: HTMLElement, onAction?: (value: string) => void) {
    this.container = container;
    this.onAction = onAction;
  }

  // ===========================================================================
  // PUBLIC METHODS
  // ===========================================================================

  /**
   * Start a new streaming response.
   */
  start(): void {
    resetRenderCycle();

    this.isCompleted = false;
    this.isDestroyed = false;
    this.content = '';
    this.processedIndex = 0;
    this.isFirstToken = true;
    this.inBlock = false;
    this.blockStartIndex = -1;
    this.blockType = '';
    this.blockPlaceholder = null;
    this.renderedBlockRanges = [];
    this.tokenBuffer = '';
    this.loadingIndicator = null;
    this.hasServerBlocks = false;
    this.collectedBlocks = [];
    this.interactiveReceived = false;
    this.thinkingWrapper = null;

    this.clearTimeouts();

    // Create response wrapper
    this.responseWrapper = document.createElement('div');
    this.responseWrapper.className = 'pms-response pms-response-streaming';

    // Start text in a thinking wrapper — if blocks arrive, this becomes
    // a collapsible "thinking" section. If not, it gets promoted to normal text.
    this.thinkingWrapper = document.createElement('div');
    this.thinkingWrapper.className = 'pms-thinking-section pms-thinking-streaming';

    this.currentTextSpan = document.createElement('div');
    this.currentTextSpan.className = 'pms-response-text pms-thinking-text';
    this.thinkingWrapper.appendChild(this.currentTextSpan);

    this.responseWrapper.appendChild(this.thinkingWrapper);
    this.container.appendChild(this.responseWrapper);
    this.scrollToBottom();
  }

  /**
   * Add a token to the streaming response.
   */
  addToken(token: string): void {
    if (this.isCompleted || this.isDestroyed) return;
    if (!this.responseWrapper || !this.currentTextSpan) return;

    // When server blocks have arrived, text tokens are filler —
    // accumulate for content tracking but don't render
    if (this.hasServerBlocks) {
      this.content += token;
      return;
    }

    this.tokenBuffer += token;

    const shouldFlushNow =
      this.tokenBuffer.length >= MIN_BUFFER_SIZE ||
      this.tokenBuffer.includes('\n') ||
      this.tokenBuffer.includes('[');

    if (shouldFlushNow) {
      this.flushBuffer();
    } else if (!this.renderTimeout) {
      this.renderTimeout = setTimeout(() => {
        this.renderTimeout = null;
        this.flushBuffer();
      }, RENDER_DELAY_MS);
    }
  }

  /**
   * Add a pre-parsed block to the response.
   */
  addBlock(block: MessageBlock): void {
    if (!this.responseWrapper) return;

    // First server block — collapse thinking text into a toggle
    if (!this.hasServerBlocks) {
      this.hasServerBlocks = true;
      this.collapseThinkingSection();
    }

    // Deep-parse block data in case AI output nested JSON as strings
    const parsedBlock: MessageBlock = {
      type: block.type,
      data: deepParseJSON(block.data)
    };

    this.collectedBlocks.push(parsedBlock);

    // Cancel any pending loading schedule (the loader persists; no need to re-create)
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }

    // Interactive blocks signal the response is effectively complete
    // (action buttons are always the last meaningful UI element).
    // Suppress further loading indicators after this point.
    if (parsedBlock.type === 'interactive') {
      this.interactiveReceived = true;
      this.hideLoadingIndicator();
    }

    const blockContainer = document.createElement('div');
    blockContainer.className = 'pms-response-block';

    const skeleton = createBlockSkeleton(parsedBlock.type);
    blockContainer.appendChild(skeleton);

    // Insert block ABOVE the loading indicator so the loader stays at the bottom.
    // Blocks stack up while the loader persists until complete().
    const insertRef = this.loadingIndicator ||
      (this.currentTextSpan && this.currentTextSpan.parentNode === this.responseWrapper
        ? this.currentTextSpan : null);

    if (insertRef) {
      this.responseWrapper.insertBefore(blockContainer, insertRef);
    } else {
      this.responseWrapper.appendChild(blockContainer);
    }

    if (this.isNearBottom()) {
      this.scrollToBottom();
    }

    // Show skeleton for a minimum time so users see the shimmer,
    // then transition to the rendered block.
    const skeletonShownAt = Date.now();
    const MIN_SKELETON_MS = 300;

    const replaceWithBlock = (): void => {
      if (this.isDestroyed) return;
      blockContainer.innerHTML = '';
      renderBlock(parsedBlock, blockContainer, this.onAction);
      if (this.isNearBottom()) {
        this.scrollToBottom();
      }
    };

    requestAnimationFrame(() => {
      if (this.isDestroyed) return;
      const elapsed = Date.now() - skeletonShownAt;
      const remaining = MIN_SKELETON_MS - elapsed;
      if (remaining > 0) {
        setTimeout(replaceWithBlock, remaining);
      } else {
        replaceWithBlock();
      }
    });
  }

  /**
   * Complete the streaming response.
   */
  complete(): void {
    if (this.isCompleted || this.isDestroyed) return;
    this.isCompleted = true;

    if (!this.responseWrapper) return;

    this.hideLoadingIndicator();
    this.clearTimeouts();

    // Flush remaining tokens
    if (this.tokenBuffer) {
      this.content += this.tokenBuffer;
      this.tokenBuffer = '';
    }

    // Clean up incomplete block
    if (this.inBlock && this.blockPlaceholder) {
      this.blockPlaceholder.remove();
      this.blockPlaceholder = null;
    }

    this.finalizeContent();

    this.responseWrapper.classList.remove('pms-response-streaming');

    // Remove cursors
    const cursors = this.responseWrapper.querySelectorAll('.pms-streaming-cursor');
    cursors.forEach(cursor => cursor.remove());

    if (this.hasServerBlocks) {
      // Server blocks present — check if thinking section has any real content
      // If not, remove it entirely for a clean look
      if (this.thinkingWrapper) {
        const thinkingText = this.thinkingWrapper.textContent?.trim();
        if (!thinkingText) {
          this.thinkingWrapper.remove();
          this.thinkingWrapper = null;
        }
      }
      // Remove any orphaned text spans outside the thinking wrapper
      const orphanedText = this.responseWrapper.querySelectorAll(':scope > .pms-response-text');
      orphanedText.forEach(span => span.remove());
    } else {
      // No server blocks — promote thinking wrapper content to normal text
      this.promoteThinkingToNormal();
      // Remove empty text spans
      const textSpans = this.responseWrapper.querySelectorAll('.pms-response-text');
      textSpans.forEach(span => {
        if (!span.textContent?.trim()) {
          span.remove();
        }
      });
    }
  }

  /**
   * Get the accumulated content.
   */
  getContent(): string {
    return this.content;
  }

  /**
   * Get all blocks that were rendered during this streaming session.
   * Used for persisting blocks alongside the message text.
   */
  getBlocks(): MessageBlock[] {
    return this.collectedBlocks;
  }

  /**
   * Show loading indicator to indicate more blocks are coming.
   * Called when the backend is making a continuation turn or
   * when tool processing is in progress (post-completion loading).
   */
  showLoading(): void {
    if (this.isDestroyed || this.interactiveReceived) return;
    this.showLoadingIndicator();
  }

  /**
   * Destroy the renderer and clean up resources.
   */
  destroy(): void {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    this.clearTimeouts();

    this.tokenBuffer = '';
    this.content = '';
    this.renderedBlockRanges = [];
    this.blockPlaceholder = null;
    this.currentTextSpan = null;
    this.loadingIndicator = null;
    this.thinkingWrapper = null;

    if (this.responseWrapper?.parentNode) {
      this.responseWrapper.remove();
    }
    this.responseWrapper = null;
  }

  // ===========================================================================
  // PRIVATE - BUFFER MANAGEMENT
  // ===========================================================================

  private flushBuffer(): void {
    if (this.isCompleted || this.isDestroyed) return;
    if (!this.tokenBuffer) return;

    this.hideLoadingIndicator();
    this.scheduleLoadingIndicator();

    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
      this.renderTimeout = null;
    }

    if (this.content.length + this.tokenBuffer.length > MAX_CONTENT_LENGTH) {
      console.warn('[StreamingRenderer] Content length exceeded limit, truncating');
      this.tokenBuffer = '';
      return;
    }

    this.content += this.tokenBuffer;
    this.tokenBuffer = '';

    this.processContent();

    if (this.isFirstToken || this.isNearBottom()) {
      this.scrollToBottom();
      this.isFirstToken = false;
    }
  }

  private clearTimeouts(): void {
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
      this.renderTimeout = null;
    }
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
  }

  // ===========================================================================
  // PRIVATE - THINKING SECTION
  // ===========================================================================

  /**
   * Collapse the thinking section into a toggleable details/summary element.
   * Called when the first server block arrives — the streamed text was filler.
   */
  private collapseThinkingSection(): void {
    if (!this.thinkingWrapper || !this.responseWrapper) return;

    // Capture the current text content (plain text for emptiness check)
    const textContent = this.thinkingWrapper.textContent?.trim() || '';

    if (!textContent) {
      // No text to show — just hide the wrapper
      this.thinkingWrapper.style.display = 'none';
      return;
    }

    // Collect already-rendered HTML from text spans, stripping cursor elements
    const textSpans = this.thinkingWrapper.querySelectorAll('.pms-response-text');
    let htmlContent = '';
    textSpans.forEach(span => {
      const html = (span as HTMLElement).innerHTML;
      const cleaned = html.replace(/<span class="pms-streaming-cursor"><\/span>/g, '');
      if (cleaned.trim()) {
        htmlContent += cleaned;
      }
    });

    // Replace with a collapsible details element
    this.thinkingWrapper.classList.remove('pms-thinking-streaming');
    this.thinkingWrapper.innerHTML = '';

    const details = document.createElement('details');
    details.className = 'pms-thinking-toggle';
    details.open = true;

    const summary = document.createElement('summary');
    summary.className = 'pms-thinking-summary';
    summary.textContent = t('blocks.thinking');
    details.appendChild(summary);

    const content = document.createElement('div');
    content.className = 'pms-thinking-content';
    // Use already-rendered HTML directly — avoids re-parsing and preserves formatting
    content.innerHTML = htmlContent || parseMarkdown(textContent);
    details.appendChild(content);

    this.thinkingWrapper.appendChild(details);

    // Create a sentinel node for block insertion ordering.
    // Blocks insert before this, so they stack in correct (arrival) order.
    this.currentTextSpan = document.createElement('div');
    this.currentTextSpan.className = 'pms-response-text pms-response-text--sentinel';
    this.currentTextSpan.style.display = 'none';
    this.responseWrapper.appendChild(this.currentTextSpan);
  }

  /**
   * Promote thinking wrapper content to normal text styling.
   * Called on complete() when no server blocks arrived.
   */
  private promoteThinkingToNormal(): void {
    if (!this.thinkingWrapper || !this.responseWrapper) return;

    // Unwrap: move children from thinking wrapper directly into response wrapper
    const children = Array.from(this.thinkingWrapper.children);
    for (const child of children) {
      child.classList.remove('pms-thinking-text');
      this.responseWrapper.insertBefore(child, this.thinkingWrapper);
    }
    this.thinkingWrapper.remove();
    this.thinkingWrapper = null;
  }

  // ===========================================================================
  // PRIVATE - LOADING INDICATOR
  // ===========================================================================

  private scheduleLoadingIndicator(): void {
    // Don't show loading after interactive block — response is effectively done
    if (this.interactiveReceived) return;

    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }

    this.loadingTimeout = setTimeout(() => {
      if (!this.isCompleted && !this.isDestroyed) {
        this.showLoadingIndicator();
      }
    }, LOADING_DELAY_MS);
  }

  private showLoadingIndicator(): void {
    if (this.loadingIndicator || !this.responseWrapper) return;

    this.loadingIndicator = createLoadingIndicator();

    // After completion, currentTextSpan may be detached from DOM.
    // Check parentNode to avoid insertBefore errors with detached references.
    if (this.currentTextSpan && this.currentTextSpan.parentNode === this.responseWrapper) {
      this.responseWrapper.insertBefore(this.loadingIndicator, this.currentTextSpan);
    } else {
      this.responseWrapper.appendChild(this.loadingIndicator);
    }

    if (this.isNearBottom()) {
      this.scrollToBottom();
    }
  }

  private hideLoadingIndicator(): void {
    if (this.loadingIndicator) {
      this.loadingIndicator.remove();
      this.loadingIndicator = null;
    }
  }

  // ===========================================================================
  // PRIVATE - CONTENT PROCESSING
  // ===========================================================================

  private processContent(): void {
    if (!this.currentTextSpan || !this.responseWrapper) return;

    // If in a block, check if it's complete
    if (this.inBlock) {
      const closePattern = new RegExp(`\\[\\/${this.blockType}\\]`, 'i');
      const searchContent = this.content.substring(this.blockStartIndex);
      const closeMatch = searchContent.match(closePattern);

      if (closeMatch && closeMatch.index !== undefined) {
        const blockEndIndex = this.blockStartIndex + closeMatch.index + closeMatch[0].length;
        const blockContent = this.content.substring(this.blockStartIndex, blockEndIndex);

        this.renderCompletedBlock(blockContent, this.blockStartIndex, blockEndIndex);

        this.inBlock = false;
        this.blockStartIndex = -1;
        this.blockType = '';
        this.processedIndex = blockEndIndex;
        this.updateTextDisplay();
      }
      return;
    }

    const unprocessedContent = this.content.substring(this.processedIndex);

    // Check for colon syntax blocks.
    // Use a non-global regex + manual scanning to avoid infinite-loop pitfalls
    // with lastIndex resets on a const string.
    const colonBlockRegex = /\[(LIVE[-_]SECTION|FLOW[-_]FORM|DEMO):([^\]\[]+)\]/i;
    let scanOffset = 0;
    let colonMatch;

    while (scanOffset < unprocessedContent.length &&
           (colonMatch = colonBlockRegex.exec(unprocessedContent.substring(scanOffset))) !== null) {
      const matchStart = scanOffset + colonMatch.index;
      const absoluteIndex = this.processedIndex + matchStart;
      const blockEndIndex = absoluteIndex + colonMatch[0].length;

      const textBefore = this.content.substring(this.processedIndex, absoluteIndex);
      if (textBefore) {
        this.appendTextContent(textBefore);
      }

      this.renderColonSyntaxBlock(colonMatch[1]!, colonMatch[2]!, absoluteIndex, blockEndIndex);

      this.processedIndex = blockEndIndex;
      scanOffset = matchStart + colonMatch[0].length;

      this.currentTextSpan = document.createElement('div');
      this.currentTextSpan.className = 'pms-response-text';
      this.responseWrapper?.appendChild(this.currentTextSpan);
    }

    // Check for JSON-based blocks
    const blockStartPattern = /\[([A-Z_-]+)\](?!\/)/gi;
    let match;
    blockStartPattern.lastIndex = 0;

    while ((match = blockStartPattern.exec(unprocessedContent)) !== null) {
      const blockType = match[1]!.toUpperCase();

      if (isColonSyntaxBlockType(blockType)) {
        continue;
      }

      if (isValidBlockType(blockType)) {
        const absoluteIndex = this.processedIndex + match.index;

        const textBefore = this.content.substring(this.processedIndex, absoluteIndex);
        if (textBefore) {
          this.appendTextContent(textBefore);
        }

        this.inBlock = true;
        this.blockType = blockType;
        this.blockStartIndex = absoluteIndex;

        this.blockPlaceholder = createBlockPlaceholder(blockType);
        this.responseWrapper?.insertBefore(this.blockPlaceholder, this.currentTextSpan);

        this.currentTextSpan = document.createElement('div');
        this.currentTextSpan.className = 'pms-response-text';
        this.responseWrapper?.appendChild(this.currentTextSpan);

        return;
      }
    }

    this.updateTextDisplay();
  }

  // ===========================================================================
  // PRIVATE - BLOCK RENDERING
  // ===========================================================================

  private renderColonSyntaxBlock(
    blockType: string,
    value: string,
    startIndex: number,
    endIndex: number
  ): void {
    if (!this.responseWrapper) return;

    const block = parseColonSyntaxBlock(blockType, value);
    if (!block) return;

    this.collectedBlocks.push(block);

    const blockContainer = document.createElement('div');
    blockContainer.className = 'pms-response-block';

    renderBlock(block, blockContainer, this.onAction);

    if (this.currentTextSpan) {
      this.responseWrapper.insertBefore(blockContainer, this.currentTextSpan);
    } else {
      this.responseWrapper.appendChild(blockContainer);
    }

    this.renderedBlockRanges.push({ start: startIndex, end: endIndex });
  }

  private renderCompletedBlock(blockContent: string, startIndex: number, endIndex: number): void {
    if (!this.responseWrapper) return;

    const parsed = parseJsonBlock(blockContent);

    if (parsed?.block) {
      this.collectedBlocks.push(parsed.block);

      const blockContainer = document.createElement('div');
      blockContainer.className = 'pms-response-block';

      renderBlock(parsed.block, blockContainer, this.onAction);

      if (this.blockPlaceholder) {
        this.blockPlaceholder.replaceWith(blockContainer);
        this.blockPlaceholder = null;
      } else {
        this.responseWrapper.insertBefore(blockContainer, this.currentTextSpan);
      }

      this.renderedBlockRanges.push({ start: startIndex, end: endIndex });
    } else {
      console.warn('[StreamingRenderer] Failed to parse block:', parsed?.error);
      if (this.blockPlaceholder) {
        const errorEl = createBlockError(this.blockType.toLowerCase());
        this.blockPlaceholder.replaceWith(errorEl);
        this.blockPlaceholder = null;
      }
    }
  }

  // ===========================================================================
  // PRIVATE - TEXT RENDERING
  // ===========================================================================

  private appendTextContent(text: string): void {
    if (!this.currentTextSpan) return;

    this.currentTextSpan.innerHTML = parseMarkdown(text);
    this.currentTextSpan.style.display = text.trim() ? '' : 'none';

    this.currentTextSpan = document.createElement('div');
    this.currentTextSpan.className = 'pms-response-text';
    this.responseWrapper?.appendChild(this.currentTextSpan);
  }

  private updateTextDisplay(): void {
    if (!this.currentTextSpan) return;

    let displayText = this.content.substring(this.processedIndex);

    // Withhold potential block markers
    const partialIndex = findPartialBlockMarker(displayText);
    if (partialIndex !== -1) {
      displayText = displayText.substring(0, partialIndex);
    }

    // Strip malformed markers
    displayText = stripMalformedStreamingMarkers(displayText);

    if (displayText) {
      this.currentTextSpan.innerHTML = parseMarkdownStreaming(displayText) +
        '<span class="pms-streaming-cursor"></span>';
    } else {
      this.currentTextSpan.innerHTML = '<span class="pms-streaming-cursor"></span>';
    }

    this.currentTextSpan.style.display = '';
  }

  // ===========================================================================
  // PRIVATE - FINALIZATION
  // ===========================================================================

  private finalizeContent(): void {
    if (!this.responseWrapper) return;

    const remainingContent = this.content.substring(this.processedIndex);

    if (!remainingContent.trim()) {
      return;
    }

    // Clear text spans (only direct children — don't touch thinking toggle content)
    const existingTextSpans = this.responseWrapper.querySelectorAll(':scope > .pms-response-text');
    existingTextSpans.forEach(span => {
      span.innerHTML = '';
    });

    // Check for missed blocks
    const colonBlockPattern = /\[(LIVE[-_]SECTION|FLOW[-_]FORM|DEMO):([^\]\[]+)\]/gi;
    const jsonBlockPattern = /\[([A-Z_-]+)\]([\s\S]*?)\[\/\1\]/gi;

    const colonMatches = remainingContent.match(colonBlockPattern);
    const jsonMatches = remainingContent.match(jsonBlockPattern);

    if ((colonMatches && colonMatches.length > 0) || (jsonMatches && jsonMatches.length > 0)) {
      this.finalizeWithBlocks(remainingContent);
    } else {
      if (this.currentTextSpan) {
        this.currentTextSpan.innerHTML = parseMarkdown(stripIncompleteBlockMarkers(remainingContent));
      }
    }
  }

  private finalizeWithBlocks(remainingContent: string): void {
    let localIndex = 0;

    if (this.currentTextSpan) {
      this.currentTextSpan.innerHTML = '';
    }

    const combinedPattern = /\[(LIVE[-_]SECTION|FLOW[-_]FORM|DEMO):([^\]\[]+)\]|\[([A-Z_-]+)\]([\s\S]*?)\[\/\3\]/gi;
    let match: RegExpExecArray | null;

    while ((match = combinedPattern.exec(remainingContent)) !== null) {
      const textBefore = remainingContent.slice(localIndex, match.index);
      if (textBefore.trim()) {
        const textEl = document.createElement('div');
        textEl.className = 'pms-response-text';
        textEl.innerHTML = parseMarkdown(textBefore);
        if (this.currentTextSpan) {
          this.responseWrapper?.insertBefore(textEl, this.currentTextSpan);
        } else {
          this.responseWrapper?.appendChild(textEl);
        }
      }

      if (match[1] && match[2]) {
        this.renderFinalColonBlock(match[1], match[2]);
      } else if (match[3] && match[4] !== undefined) {
        this.renderFinalJsonBlock(match[3], match[4]);
      }

      localIndex = match.index + match[0].length;
    }

    const textAfter = stripIncompleteBlockMarkers(remainingContent.slice(localIndex));
    if (textAfter.trim() && this.currentTextSpan) {
      this.currentTextSpan.innerHTML = parseMarkdown(textAfter);
    }
  }

  private renderFinalColonBlock(blockType: string, value: string): void {
    const block = parseColonSyntaxBlock(blockType, value);

    if (block) {
      this.collectedBlocks.push(block);
      const blockContainer = document.createElement('div');
      blockContainer.className = 'pms-response-block';
      renderBlock(block, blockContainer, this.onAction);
      if (this.currentTextSpan) {
        this.responseWrapper?.insertBefore(blockContainer, this.currentTextSpan);
      } else {
        this.responseWrapper?.appendChild(blockContainer);
      }
    } else if (blockType.toLowerCase().replace(/_/g, '-') === 'demo') {
      // Trigger demo switch action
      if (this.onAction) {
        this.onAction(`demo:${value.trim()}`);
      }
    }
  }

  private renderFinalJsonBlock(blockType: string, blockDataStr: string): void {
    const normalizedType = blockType.toLowerCase();
    const blockData = tryParseJSON(blockDataStr.trim());

    if (blockData !== null) {
      const block: MessageBlock = { type: normalizedType, data: deepParseJSON(blockData) };
      this.collectedBlocks.push(block);
      const blockContainer = document.createElement('div');
      blockContainer.className = 'pms-response-block';
      renderBlock(block, blockContainer, this.onAction);
      if (this.currentTextSpan) {
        this.responseWrapper?.insertBefore(blockContainer, this.currentTextSpan);
      } else {
        this.responseWrapper?.appendChild(blockContainer);
      }
    } else {
      const errorEl = createBlockError(normalizedType);
      if (this.currentTextSpan) {
        this.responseWrapper?.insertBefore(errorEl, this.currentTextSpan);
      } else {
        this.responseWrapper?.appendChild(errorEl);
      }
    }
  }

  // ===========================================================================
  // PRIVATE - SCROLL MANAGEMENT
  // ===========================================================================

  private scrollToBottom(): void {
    const messagesContainer = this.container.closest('.pms-panel-messages');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }

  private isNearBottom(): boolean {
    const messagesContainer = this.container.closest('.pms-panel-messages');
    if (!messagesContainer) return true;
    const threshold = 100;
    return messagesContainer.scrollHeight - messagesContainer.scrollTop - messagesContainer.clientHeight < threshold;
  }
}
